import { useRef, useEffect, useState, useCallback } from 'react'
import { createUseStyles } from 'react-jss'
import gsap from 'gsap'
import { useSelector, shallowEqual, useDispatch } from 'react-redux'
import useUpdateEffect from '@/hooks/useUpdateEffect'
import Form from '@/components/Form'
import actions from '@/actions'
import { getCookie, setCookie } from '@/utils/cookie'
import utils from '@/utils'
import style from './style'

const useStyles = createUseStyles(style)

const NewsletterPopup = ({
  className,
  timeout,
}) => {
  const classes = useStyles(style)
  const $root = useRef()
  const $close = useRef()
  const [popupOpen, setPopupOpen] = useState(false)
  const [feedback, setFeedback] = useState({})

  /*------------------------------
  Redux Store
  ------------------------------*/
  const { strings, response, form } = useSelector((state) => ({
    footerNav: state.nav.footerMenu || [],
    copyrightNav: state.nav.copyrightMenu || [],
    strings: state.language.string,
    response: state.page.status_form,
    form: state.page.forms,
  }), shallowEqual)

  /*------------------------------
  Redux Actions
  ------------------------------*/
  const dispatch = useDispatch()
  const resetForm = useCallback(() => dispatch(actions.page.resetForm()), [dispatch])
  const sendForm = useCallback((index, params) => dispatch(actions.page.sendForm(index, params)), [dispatch])
  const sendDataToCallback = useCallback((data) => dispatch(actions.page.sendDataToCallback(data)), [dispatch])
  const getForm = useCallback((index) => dispatch(actions.page.getDataForm(index)), [dispatch])

  useEffect(() => {
    getForm(strings.newsletter_id_form)
  }, [])

  useUpdateEffect(() => {
    setFeedback(response)
  }, [response])

  /*------------------------------
  Close Modal
  ------------------------------*/
  const closeModal = () => {
    setPopupOpen(false)
  }

  /*------------------------------
  HandleKeyboard
  ------------------------------*/
  const handleKeyboard = (e) => {
    // Esc = 27
    if (e.which === 27) {
      closeModal()
    }
  }

  /*------------------------------
  HandleClose
  ------------------------------*/
  const handleClose = () => {
    closeModal()
  }

  /*------------------------------
  Events
  ------------------------------*/
  const events = () => {
    window.addEventListener('keydown', handleKeyboard)
  }

  /*------------------------------
  Destroy Events
  ------------------------------*/
  const destroyEvents = () => {
    window.removeEventListener('keydown', handleKeyboard)
  }

  /*------------------------------
  Apri Chiudi
  ------------------------------*/
  useEffect(() => {
    if (popupOpen === true) {
      gsap.to($root.current, {
        duration: 0.5,
        autoAlpha: 1,
        pointerEvents: 'all',
        display: 'block',
      })
      gsap.fromTo($close.current, {
        rotation: -90,
        scale: 0.5,
      }, {
        duration: 0.6,
        rotation: 0,
        scale: 1,
        ease: 'power3.out',
      })

      events()
    } else {
      gsap.to($root.current, {
        duration: 0.5,
        autoAlpha: 0,
        display: 'none',
        pointerEvents: 'none',
      })
      gsap.to($close.current, {
        duration: 0.6,
        rotation: 90,
        scale: 0.5,
        ease: 'power3.out',
      })

      destroyEvents()
    }
  }, [popupOpen])

  /*------------------------------
  Cookie & Timer
  ------------------------------*/
  useEffect(() => {
    if (strings.newsletter_copy && !getCookie('newsletter-popup')) {
      setTimeout(() => {
        setPopupOpen(true)
        setCookie('newsletter-popup', 'true', '365')
      }, timeout)
    }
  }, [strings])

  /*------------------------------
  Render
  ------------------------------*/
  return (
    <div
      className={`${classes.root} ${className}`}
      ref={$root}
      role="dialog"
      aria-modal="true"
    >
      <div className={classes.wrapper}>
        <button
          className={classes.close}
          onClick={handleClose}
          ref={$close}
          aria-label="close-modal"
          tabIndex={popupOpen ? 0 : -1}
        >
          <svg>
            <use xlinkHref="#ico-close" />
          </svg>
        </button>
        <div className={classes.content}>
          {
            strings.newsletter_popup_image && (
              <div className={classes.image}>
                <img src={strings.newsletter_popup_image} alt="newsletter" />
              </div>
            )
          }
          <div className={classes.box}>
            <div
              className={classes.copy}
              dangerouslySetInnerHTML={{ __html: strings.newsletter_copy }}
            />
            <div className={classes.form}>
              {form?.[strings.newsletter_id_form] && (
              <Form
                data={form[strings.newsletter_id_form].data}
                newsletter={strings.newsletter_privacy_label}
                newsletterLink={utils.path.cleanOrigin(strings.newsletter_privacy_link)}
                labelSubmit={strings.newsletter_privacy_send}
                resetForm={resetForm}
                sendForm={(value) => sendForm(strings.newsletter_id_form, value)}
                parent={this}
                response={feedback}
                mailchimpCallback={(data) => sendDataToCallback(data)}
              />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default NewsletterPopup
